import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UserService } from '../../services/user.service';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { LogoComponent } from "src/app/components/logo/logo.component";
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { UserProfileAvatarComponent } from "../../components/user-profile-avatar/user-profile-avatar.component";
import { TokenService } from '../../services/token.service';

/**
 * UserApplicationReportComponent
 * * Generates a read-only, printer-friendly summary of the user's latest application.
 * Responsibilities:
 * - Fetching application data.
 * - Processing raw data into a display-friendly report structure.
 * - Handling print actions (window.print).
 * - Redirecting to the edit page if the application status allows it.
 */
@Component({
  selector: 'app-user-application-report',
  standalone: true,
  imports: [CommonModule, RouterLink, NzSkeletonModule, LogoComponent, NgxMaskPipe, UserProfileAvatarComponent],
  providers: [provideNgxMask()],
  templateUrl: './user-application-report.component.html',
  styles: [`
    /* Google Font for Signature Display */
    @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');

    .signature-font {
      font-family: 'Great Vibes', cursive;
      font-size: 2rem;
    }

    /* --- PRINT SPECIFIC STYLES --- */
    /* Optimizes layout for standard paper sizes, hiding UI elements like sidebar */
    @media print {
      @page {
        margin: 0.5in;
        size: auto;
      }
      aside, .no-print { 
        display: none !important; 
      }
      main {
        margin-left: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        height: auto !important;
        width: 100% !important;
        display: block !important;
      }
      body, .bg-gray-50, { 
        background-color: white !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .print-card {
        box-shadow: none !important;
        border: none !important;
        border-bottom: 1px solid #eee !important;
        margin-bottom: 10px !important;
        padding: 10px 0 !important;
        border-radius: 0 !important;
        break-inside: auto;
      }
      .print-grid-3 {
        display: grid !important;
        grid-template-columns: repeat(3, 1fr) !important;
        gap: 10px !important;
      }
      .print-grid-2 {
        display: grid !important;
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 15px !important;
      }
      .avoid-break {
        break-inside: avoid !important;
      }
    }
  `]
})
export class UserApplicationReportComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private userService = inject(UserService);
  private notification = inject(NzNotificationService);
  private tokenService = inject(TokenService);

  // --- UI State ---
  /** Controls skeleton loading state */
  isLoading = signal(true);

  // --- Data Context ---
  /** ID of the user being viewed */
  targetUserId = '';
  /** Full user profile object */
  targetUser: any = null;
  /** Processed object specifically for the report view template */
  reportData: any = null;
  /** Flag indicating if the user has permission to edit this application */
  canEdit = false;

  /**
   * Lifecycle Hook: Initialize component logic.
   */
  ngOnInit() {
    this.targetUserId = this.route.snapshot.paramMap.get('userId') || '';
    if (this.targetUserId) {
      this.loadReportData();
    } else {
      this.notification.error('Error', 'No User ID provided');
      this.router.navigate(['/intake']);
    }
  }

  /**
   * Fetches user data and finds the active application to display.
   */
  loadReportData() {
    this.isLoading.set(true);
    this.userService.getUserById(this.targetUserId).subscribe({
      next: (user) => {
        this.targetUser = user;
        const apps = user.applications?.filter((a: any) => !a.isDeleted);

        if (apps && apps.length > 0) {
          // Sort to find latest
          apps.sort((a: any, b: any) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
          this.processApplicationData(apps[0]);
        } else {
          this.notification.warning('Notice', 'No active application found for this user.');
          this.reportData = null;
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.notification.error('Error', 'Failed to load report data');
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Logs out the current user.
   */
  logout() {
    this.tokenService.clearToken();
    this.router.navigate(['/login']);
  }

  /**
   * Converts the API application object into a flat, safe 'Report Data' object.
   * Handles null checks, date formatting, and data flattening for the view.
   * * @param app - The application object from the API.
   */
  processApplicationData(app: any) {
    const val = (v: any, def: any = '-') => (v !== null && v !== undefined && v !== '') ? v : def;
    const bool = (v: any) => !!v;

    // Determine signature display (fallback to name if empty)
    let finalSignature = app.electronicSignature;
    if (!finalSignature || finalSignature.trim() === '') {
      const fName = app.firstName || this.targetUser?.firstName || '';
      const lName = app.lastName || this.targetUser?.lastName || '';
      finalSignature = `${fName} ${lName}`.trim();
    }

    // Determine edit permission based on status
    const editableStatuses = ['Draft', 'Pre-Submission Review', 'Awaiting Applicant Updates'];
    this.canEdit = editableStatuses.includes(app.status);

    this.reportData = {
      id: app.id,
      peNumber: val(app.peNumber, null),
      updateRequestNotes: val(app.updateRequestNotes, null), // Map the notes field
      status: val(app.status, 'Draft'),
      submittedDate: app.updatedDate ?? app.createdDate ?? app.submittedDate ? val(app.updatedDate ?? app.createdDate ?? app.submittedDate) : null,
      type: val(app.type, 'My Self'),
      zipcode: val(app.zipcode),
      homeAddress: {
        street1: val(app.homeAddress1),
        street2: val(app.homeAddress2, ''),
        city: val(app.city),
        state: val(app.homeState),
        zip: val(app.zipcode)
      },
      mailingAddress: {
        street1: val(app.mailingAddress1, ''),
        street2: val(app.mailingAddress2, ''),
        city: val(app.mailingCity, ''),
        state: val(app.mailingState, ''),
        zip: val(app.mailingZipCode, '')
      },
      mailingSameAsHome: !app.mailingAddress1,
      contact: {
        primaryPhone: val(this.targetUser.phoneNumber),
        primaryType: 'MOBILE',
        secondaryPhone: val(app.secondaryPhone, '-'),
        secondaryType: val(app.secondaryPhoneType, '-'),
        email: val(this.targetUser.email)
      },
      // Map household members to display format
      members: (app.householdMembers || []).map((m: any, index: number) => ({
        index: index + 1,
        name: `${m.firstName} ${m.middleName ? m.middleName + ' ' : ''}${m.lastName} ${m.suffix || ''}`,
        role: m.isApplicant ? 'Primary Applicant' : 'Member',
        dob: m.dateOfBirth ? new Date(m.dateOfBirth).toLocaleDateString() : '-',
        gender: val(m.gender),
        marital: val(m.maritalStatus),
        ssn: m.ssn && m.ssn.length >= 4 ? `***-**-${m.ssn.slice(-4)}` : (m.ssn || ''),
        citizenship: val(m.citizenshipStatus).replace('_', ' '),
        student: m.isStudent ? 'Yes' : 'No',
        height: (m.heightFt || m.heightIn) ? `${m.heightFt || 0}' ${m.heightIn || 0}"` : '-',
        weight: m.weightLbs ? `${m.weightLbs} lbs` : '-'
      })),
      financial: {
        grossAnnual: val(app.grossAnnualIncome || app.houseHoldIncomeRange, '$0.00'),
        monthlyCalculated: val(app.grossMonthlyIncome, '$0.00'),
        medicareCredits: val(app.medicareCredits),
        assets: val(app.householdAssets),
        workHours: val(app.weeklyWorkHours),
        filingStatus: val(app.taxFilingStatus).replace(/_/g, ' ')
      },
      healthQuestions: [
        { label: 'Smoker', value: bool(app.smoking) },
        { label: 'Pregnant', value: bool(app.pregnancy) },
        { label: 'Disabled', value: bool(app.isDisabled) },
        { label: 'Blind', value: bool(app.isBlind) },
        { label: 'Diabetic', value: bool(app.isDiabetic) },
        { label: 'Postpartum', value: bool(app.isPostpartumPeriod) },
        { label: 'Mood Disorder', value: bool(app.hasMoodDisorder) },
        { label: 'Brain/Spinal Injury', value: bool(app.hasBrainSpinalInjury) },
        { label: 'Life Limiting Illness', value: bool(app.hasLifeLimitingIllness) },
        { label: 'Vehicle Owner', value: bool(app.hasVehicle) },
        { label: 'Military Affiliation', value: bool(app.hasMilitaryAffiliation) },
        { label: 'Breast/Cervical Cancer', value: bool(app.hasBreastCervicalCancer) },
        { label: 'HIV/AIDS', value: bool(app.hasHIVAIDSDiagnosis) },
        { label: 'Genetic Disorder', value: bool(app.hasGeneticDisorder) },
        { label: 'Substance Use Disorder', value: bool(app.hasSubstanceUseDisorder) },
        { label: 'Rehabilitation Need', value: bool(app.hasRehabilitationNeed) },
        { label: 'Foster Care', value: bool(app.isInFosterCare) },
        { label: 'Tribal Member', value: bool(app.hasTribalMembership) },
      ],
      coverage: {
        hasInsurance: bool(app.hasCurrentInsurance),
        hasMedicare: bool(app.hasMedicare)
      },
      qualifyingEvent: val(app.qualifyingLifeEvent || app.lifeEvent, 'None'),
      conditions: (app.healthConditions && app.healthConditions.length) ? app.healthConditions.join(', ') : 'None',
      attestation: {
        agreed: bool(app.attestationAgreed),
        signature: val(finalSignature, ''),
        date: app.attestationDate ? new Date(app.attestationDate).toLocaleDateString() :
          app.updatedDate ? new Date(app.updatedDate).toLocaleDateString() :
            app.createdDate ? new Date(app.createdDate).toLocaleDateString() :
              '-'
      }
    };
  }

  /**
   * Triggers the browser's print dialog.
   */
  printReport() {
    window.print();
  }

  /**
   * Navigates back to the Dashboard.
   */
  backToDashboard() {
    this.router.navigate(['/intake']);
  }

  /**
   * Navigates to the Edit view for this application.
   */
  navigateToEdit() {
    if (this.canEdit) {
      this.router.navigate(['/intake/user', this.targetUserId]);
    }
  }
}